import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { CALLABLE_BEER_ADD_BEER, functions } from '../utils/firebase';
import { processImportResponse } from '../utils/addBeerResponse';
import {
  getUserFacingCallableError,
  isTransientCallableError,
} from '../utils/callableErrorMessages';
import * as Sentry from '@sentry/react';

function trimIfString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function buildPayloadBeer({ beer, breweryIdTrim, forceAddDuplicate = false }) {
  const nameTrim = trimIfString(beer?.name);
  const styleTrim = trimIfString(beer?.style);

  const payloadBeer = {
    name: nameTrim,
    style: styleTrim,
    abv: beer.abv,
    breweryId: breweryIdTrim,
  };
  if (beer.description != null && String(beer.description).trim() !== '') {
    payloadBeer.description = String(beer.description).trim();
  }
  const styleNameTrim = trimIfString(beer?.styleName);
  const styleTypeTrim = trimIfString(beer?.styleType);
  if (styleNameTrim && styleTypeTrim) {
    payloadBeer.styleName = styleNameTrim;
    payloadBeer.styleType = styleTypeTrim;
  }
  if (forceAddDuplicate === true) {
    payloadBeer.forceAddDuplicate = true;
  }
  return payloadBeer;
}

function appendAvailableBreweryIds(requestBody, availableBreweryIds) {
  if (Array.isArray(availableBreweryIds) && availableBreweryIds.length > 0) {
    requestBody.availableBreweryIds = availableBreweryIds
      .filter((breweryIdValue) => typeof breweryIdValue === 'string')
      .map((breweryIdValue) => breweryIdValue.trim())
      .filter(Boolean);
  }
  return requestBody;
}

function buildAddBeerRequestBody({
  useReviewForce,
  breweryId,
  reviewIdTrim,
  uid,
  beer,
  forceAddDuplicate,
  sourceType = 'engage_user',
  availableBreweryIds,
}) {
  const breweryIdTrim = trimIfString(breweryId);
  const uidTrim = trimIfString(uid);

  if (useReviewForce) {
    const requestBody = {
      breweryId: breweryIdTrim,
      beerDuplicateReviewId: reviewIdTrim,
      forceAddDuplicate: true,
      source: uidTrim,
      sourceType: 'dupe_override',
    };
    appendAvailableBreweryIds(requestBody, availableBreweryIds);
    if (beer && typeof beer === 'object') {
      requestBody.beers = [
        buildPayloadBeer({ beer, breweryIdTrim }),
      ];
    }
    return requestBody;
  }

  const payloadBeer = buildPayloadBeer({
    beer,
    breweryIdTrim,
    forceAddDuplicate,
  });

  const requestBody = {
    beers: [payloadBeer],
    breweryId: breweryIdTrim,
    source: uidTrim,
    sourceType: typeof sourceType === 'string' && sourceType.trim()
      ? sourceType.trim()
      : 'engage_user',
  };
  appendAvailableBreweryIds(requestBody, availableBreweryIds);
  if (forceAddDuplicate === true) {
    requestBody.forceAddDuplicate = true;
  }
  return requestBody;
}

function logAddBeerDevSummary({
  forceAddDuplicate,
  useReviewForce,
  requestBody,
  reviewIdTrim,
  normalized,
  raw,
}) {
  const summary = {
    forceAddDuplicate: Boolean(forceAddDuplicate),
    useReviewForce,
    sourceType: requestBody.sourceType,
    beerDuplicateReviewId: useReviewForce ? reviewIdTrim : '',
    addedCount: normalized.addedCount,
    updatedCount: normalized.updatedCount,
    duplicatedCount: normalized.duplicatedCount,
    errorCount: normalized.errorCount,
    status: normalized.status,
    normalizedReviewId: normalized.beerDuplicateReviewId,
  };
  const firstDup = Array.isArray(raw.duplicatedBeers) ? raw.duplicatedBeers[0] : null;
  // eslint-disable-next-line no-console
  console.info('[useBeerAddBeer] addBeer callable finished', summary, {
    rawResponseKeys: raw && typeof raw === 'object' ? Object.keys(raw) : [],
    firstDuplicatedBeerKeys:
      firstDup && typeof firstDup === 'object' ? Object.keys(firstDup) : [],
    raw,
  });
}

/**
 * useBeerAddBeer
 * Single-beer add via addBeer Cloud Function (engage_user source).
 *
 * @returns {{ addSingleBeer: Function, loading: boolean, errorMessage: string, clearError: Function }}
 */
export function useBeerAddBeer() {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const isMountedRef = useRef(true);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const clearError = useCallback(() => {
    setErrorMessage('');
  }, []);

  /**
   * @param {Object} params
   * @param {string} params.breweryId
   * @param {string} params.uid - Firebase Auth uid for `source`
   * @param {{ name: string, style: string, abv: number, description?: string|null }} [params.beer]
   * @param {boolean} [params.forceAddDuplicate]
   * @param {string} [params.beerDuplicateReviewId] - When forcing, if the duplicate response
   * included a review doc id, pass it so the callable uses the same body as addBeerAndCompleteReview.
   * @returns {Promise<{ ok: true, normalized: object, raw: object }|{ ok: false, error: string }>}
   */
  const addSingleBeer = useCallback(async ({
    breweryId,
    uid,
    beer,
    forceAddDuplicate = false,
    beerDuplicateReviewId = '',
    sourceType = 'engage_user',
    availableBreweryIds = null,
  }) => {
    if (!breweryId || typeof breweryId !== 'string' || !breweryId.trim()) {
      return { ok: false, error: 'Brewery ID is required' };
    }
    if (!uid || typeof uid !== 'string' || !uid.trim()) {
      return { ok: false, error: 'You must be signed in to add a beer.' };
    }

    const reviewIdTrim =
      typeof beerDuplicateReviewId === 'string' ? beerDuplicateReviewId.trim() : '';
    const useReviewForce = forceAddDuplicate === true && reviewIdTrim.length > 0;

    if (!useReviewForce) {
      if (!beer || typeof beer !== 'object') {
        return { ok: false, error: 'Invalid beer data' };
      }
      if (typeof beer.name !== 'string' || typeof beer.style !== 'string') {
        return {
          ok: false,
          error: 'Beer name and style must be valid text.',
        };
      }
    }

    if (!isMountedRef.current) {
      return { ok: false, error: 'component unmounted' };
    }

    if (isSubmittingRef.current) {
      return {
        ok: false,
        error: 'A beer add request is already in progress.',
      };
    }

    isSubmittingRef.current = true;
    setLoading(true);
    setErrorMessage('');

    try {
      const addBeerFn = httpsCallable(functions, CALLABLE_BEER_ADD_BEER, { timeout: 600000 });

      const requestBody = buildAddBeerRequestBody({
        useReviewForce,
        breweryId,
        reviewIdTrim,
        uid,
        beer,
        forceAddDuplicate,
        sourceType,
        availableBreweryIds,
      });

      const result = await addBeerFn(requestBody);

      const raw = result?.data || {};
      const normalized = processImportResponse(raw);

      if (import.meta.env.DEV) {
        logAddBeerDevSummary({
          forceAddDuplicate,
          useReviewForce,
          requestBody,
          reviewIdTrim,
          normalized,
          raw,
        });
      }

      return { ok: true, normalized, raw };
    } catch (err) {
      Sentry.captureException(err);
      const isTransient = isTransientCallableError(err);
      const message = getUserFacingCallableError(err, { operation: 'addBeer' });
      if (isMountedRef.current) {
        setErrorMessage(message);
      }
      return { ok: false, error: message, isTransient, rawError: err };
    } finally {
      isSubmittingRef.current = false;
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  return {
    addSingleBeer,
    loading,
    errorMessage,
    clearError,
  };
}
